import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import NewsCard from "@/components/public/NewsCard";
import AdSlot from "@/components/public/AdSlot";
import { Search } from "lucide-react";

const CATEGORIES = ["All", "News", "Game", "Esports", "Updates", "Industry"];

export default function NewsList() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const sentinel = useRef(null);
  const PAGE = 12;

  const fetchPage = (skip) => {
    const params = { limit: PAGE, skip };
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    return api.get("/news", { params });
  };

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchPage(0).then((r) => { setItems(r.data.items); setTotal(r.data.total); }).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || more || loading || items.length >= total) return;
      setMore(true);
      fetchPage(items.length).then((r) => setItems((cur) => [...cur, ...r.data.items.filter((a) => !cur.some((c) => c.id === a.id))])).catch(() => {}).finally(() => setMore(false));
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [items, total, more, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Newsroom</span>
          <h1 className="mt-2 text-4xl font-bold text-white">Latest News</h1>
          <p className="mt-2 text-white/50">The latest stories, updates, and coverage across the gaming world.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="news-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search news…"
            className="w-full pl-9 pr-3 py-2.5 rounded-full card-surface text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`cat-${c.toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              cat === c ? "bg-white text-black" : "card-surface text-white/60 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <AdSlot className="my-8" style={{ minHeight: 100 }} />

      {loading ? (
        <div className="py-20 text-center text-white/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-white/40">No articles found.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((a) => <NewsCard key={a.id} article={a} />)}
        </div>
      )}
      <div ref={sentinel} className="py-10 text-center text-sm text-white/40" data-testid="news-sentinel">
        {more ? "Loading more…" : !loading && items.length > 0 && items.length >= total ? "You're all caught up." : ""}
      </div>
    </div>
  );
}
